import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { Evolucao } from "../../../types";
import { useAuth } from "../../auth/context/AuthContext";
import { db } from "../../../lib/firebase";
import {
  registrarEdicaoTrilha,
  registrarExclusaoTrilha,
} from "../../../shared/utils/trilhaAuditoria";
import { calcularCamposAlterados } from "../../../shared/utils/trilhaAuditoriaDiff";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";

interface EvolucaoContextProps {
  evolucoes: Evolucao[];
  loading: boolean;
  adicionarEvolucao: (
    pacienteId: string,
    dados: Omit<
      Evolucao,
      "id" | "data" | "registradoPor" | "registradoPorNome"
    > & { dataManual?: string }
  ) => Promise<void>;
  listarEvolucoesPorPaciente: (pacienteId: string) => Evolucao[];
  editarEvolucao: (id: string, dados: Partial<Evolucao>) => Promise<void>;
  cancelarEvolucao: (id: string, motivo: string) => Promise<void>;
  removerEvolucao: (id: string) => Promise<void>;
  carregarEvolucoes: () => Promise<void>;
}

const EvolucaoContext = createContext<EvolucaoContextProps | undefined>(
  undefined
);

export function EvolucaoProvider({ children }: { children: ReactNode }) {
  const { usuarioLogado } = useAuth();
  const [evolucoes, setEvolucoes] = useState<Evolucao[]>([]);
  const [loading, setLoading] = useState(true);

  // ============================================
  // 📥 CARREGAR EVOLUÇÕES DO FIREBASE
  // ============================================
  const carregarEvolucoes = async () => {
    try {
      setLoading(true);
      console.log("🔍 Carregando evoluções do Firebase...");

      const querySnapshot = await getDocs(collection(db, "evolucoes"));

      const evolucoesFormatadas: Evolucao[] = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          pacienteId: data.pacienteId,
          data: data.data?.toDate?.()?.toISOString?.() || data.data,
          queixaPrincipal: data.queixaPrincipal,
          historiaDoencaAtual: data.historiaDoencaAtual,
          revisaoSistemas: data.revisaoSistemas,
          exameFisico: data.exameFisico,
          hipotesesDiagnosticas: data.hipotesesDiagnosticas,
          conduta: data.conduta,
          observacoes: data.observacoes,
          peso: data.peso,
          altura: data.altura,
          fc: data.fc,
          pa: data.pa,
          registradoPor: data.registradoPor,
          registradoPorNome: data.registradoPorNome,
          editadoPor: data.editadoPor,
          editadoPorNome: data.editadoPorNome,
          dataEdicao:
            data.dataEdicao?.toDate?.()?.toISOString?.() || data.dataEdicao,
          // B323 Bloco 3 (AI_146): campos de cancelamento
          cancelado: data.cancelado,
          canceladoPor: data.canceladoPor,
          canceladoPorNome: data.canceladoPorNome,
          canceladoEm:
            data.canceladoEm?.toDate?.()?.toISOString?.() || data.canceladoEm,
          motivoCancelamento: data.motivoCancelamento,
          analisePorIA: data.analisePorIA,
        } as unknown as Evolucao;
      });

      setEvolucoes(evolucoesFormatadas);
      console.log("✅ Evoluções carregadas:", evolucoesFormatadas.length);
    } catch (error) {
      console.error("❌ Erro ao carregar evoluções:", error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // 🔄 CARREGAR AO MONTAR
  // ============================================
  useEffect(() => {
    if (!usuarioLogado) {
      setLoading(false);
      return;
    }

    setLoading(true);
    console.log("🔍 Escutando evoluções em tempo real...");

    const unsubscribe = onSnapshot(
      collection(db, "evolucoes"),
      (snapshot) => {
        const lista: Evolucao[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            data: data.data?.toDate?.()?.toISOString?.() || data.data,
            criadoEm:
              data.criadoEm?.toDate?.()?.toISOString?.() || data.criadoEm,
            dataEdicao:
              data.dataEdicao?.toDate?.()?.toISOString?.() || data.dataEdicao,
            canceladoEm:
              data.canceladoEm?.toDate?.()?.toISOString?.() ||
              data.canceladoEm,
          } as unknown as Evolucao;
        });

        setEvolucoes(lista);
        setLoading(false);
        console.log("✅ Evoluções atualizadas (tempo real):", lista.length);
      },
      (error) => {
        console.error("❌ Erro no listener de evoluções:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [usuarioLogado]);

  // ============================================
  // ➕ ADICIONAR EVOLUÇÃO
  // ============================================
  const adicionarEvolucao = async (
    pacienteId: string,
    dados: Omit<
      Evolucao,
      "id" | "data" | "registradoPor" | "registradoPorNome"
    > & { dataManual?: string }
  ) => {
    if (!usuarioLogado) {
      console.error("❌ Usuário não está logado");
      throw new Error("Usuário não está logado");
    }

    try {
      console.log("📝 Adicionando evolução...");

      await addDoc(collection(db, "evolucoes"), {
        pacienteId,
        data: dados.dataManual ? new Date(dados.dataManual) : serverTimestamp(),
        queixaPrincipal: dados.queixaPrincipal || null,
        historiaDoencaAtual: dados.historiaDoencaAtual || null,
        revisaoSistemas: dados.revisaoSistemas || null,
        exameFisico: dados.exameFisico || null,
        hipotesesDiagnosticas: dados.hipotesesDiagnosticas || null,
        conduta: dados.conduta || null,
        observacoes: dados.observacoes || null,
        tipoConsulta: dados.tipoConsulta || null,
        evolucaoLivre: dados.evolucaoLivre || null,
        peso: dados.peso || null,
        altura: dados.altura || null,
        fc: dados.fc || null,
        pa: dados.pa || null,
        registradoPor: usuarioLogado.id,
        registradoPorNome: usuarioLogado.nome,
        criadoEm: serverTimestamp(),
      });

      console.log("✅ Evolução adicionada com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao adicionar evolução:", error);
      throw error;
    }
  };

  // ============================================
  // 📋 LISTAR EVOLUÇÕES DE UM PACIENTE
  // ============================================
  const listarEvolucoesPorPaciente = (pacienteId: string): Evolucao[] => {
    return evolucoes
      .filter((ev) => ev.pacienteId === pacienteId)
      .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());
  };

  // ============================================
  // ✏️ EDITAR EVOLUÇÃO
  // ============================================
  const editarEvolucao = async (id: string, dados: Partial<Evolucao>) => {
    if (!usuarioLogado) {
      console.error("❌ Usuário não está logado");
      throw new Error("Usuário não está logado");
    }

    try {
      console.log("📝 Editando evolução:", id);

      const dadosFirebase: any = {
        editadoPor: usuarioLogado.id,
        editadoPorNome: usuarioLogado.nome,
        dataEdicao: serverTimestamp(),
      };

      // Adiciona apenas campos que foram alterados.
      // FIX preventivo (AI_99, mesmo bug achado em marcadores/sorologias
      // no teste B213): `criadoEm` volta da tela convertido p/ string e a
      // imutabilidade do B81 negaria o update inteiro. Campos imutáveis/
      // metadados ficam fora do payload.
      const CAMPOS_BLOQUEADOS = new Set([
        "id",
        "data",
        "registradoPor",
        "registradoPorNome",
        "criadoEm",
        "dataEdicao",
        "editadoPor",
        "editadoPorNome",
        "pacienteId",
      ]);
      Object.keys(dados).forEach((key) => {
        const value = (dados as any)[key];
        if (value !== undefined && !CAMPOS_BLOQUEADOS.has(key)) {
          dadosFirebase[key] = value;
        }
      });

      await updateDoc(doc(db, "evolucoes", id), dadosFirebase);

      // B213 — trilha de auditoria (diff por valor contra o doc em memória)
      const atual = evolucoes.find((ev) => ev.id === id);
      await registrarEdicaoTrilha({
        colecao: "evolucoes",
        docId: id,
        pacienteId: atual?.pacienteId || "",
        rotulo: atual?.data
          ? `Consulta de ${new Date(atual.data).toLocaleDateString("pt-BR")}`
          : "Consulta",
        usuario: { id: usuarioLogado.id, nome: usuarioLogado.nome },
        camposAlterados: calcularCamposAlterados(dados, atual),
      });

      console.log("✅ Evolução editada com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao editar evolução:", error);
      throw error;
    }
  };

  // ============================================
  // ⊘ CANCELAR EVOLUÇÃO (B323 Bloco 3, AI_146)
  // ============================================
  // Correção NÃO-destrutiva de registro antigo: a consulta fica no banco,
  // marcada como cancelada, com autor e MOTIVO (nunca some — retenção CFM;
  // some das telas normais e da coleta para a IA). Espelha o cancelamento
  // validado nas prescrições (AI_144). Substitui a sobrescrita/exclusão crua:
  // nem o admin reescreve conteúdo finalizado — corrige por aqui.
  const cancelarEvolucao = async (id: string, motivo: string) => {
    if (!usuarioLogado) {
      console.error("❌ Usuário não está logado");
      throw new Error("Usuário não está logado");
    }
    const atual = evolucoes.find((ev) => ev.id === id);
    if (!atual || atual.cancelado) return;
    const motivoLimpo = (motivo || "").trim();
    if (!motivoLimpo) {
      throw new Error("É obrigatório informar o motivo do cancelamento.");
    }
    try {
      await updateDoc(doc(db, "evolucoes", id), {
        cancelado: true,
        canceladoPor: usuarioLogado.id,
        canceladoPorNome: usuarioLogado.nome,
        canceladoEm: serverTimestamp(),
        motivoCancelamento: motivoLimpo,
      });
      await registrarEdicaoTrilha({
        colecao: "evolucoes",
        docId: id,
        pacienteId: atual.pacienteId || "",
        rotulo: atual.data
          ? `Consulta de ${new Date(atual.data).toLocaleDateString(
              "pt-BR"
            )} — CANCELADA`
          : "Consulta — CANCELADA",
        usuario: { id: usuarioLogado.id, nome: usuarioLogado.nome },
        camposAlterados: ["cancelado"],
      });
      console.log("✅ Evolução cancelada.");
    } catch (error) {
      console.error("❌ Erro ao cancelar evolução:", error);
      throw error;
    }
  };

  // ============================================
  // 🗑️ REMOVER EVOLUÇÃO
  // ============================================
  const removerEvolucao = async (id: string) => {
    if (!usuarioLogado) {
      console.error("❌ Usuário não está logado");
      throw new Error("Usuário não está logado");
    }

    try {
      console.log("🗑️ Removendo evolução:", id);

      // B213 — captura os dados ANTES do delete (a lápide sobrevive ao doc)
      const atual = evolucoes.find((ev) => ev.id === id);

      await deleteDoc(doc(db, "evolucoes", id));

      await registrarExclusaoTrilha({
        colecao: "evolucoes",
        docId: id,
        pacienteId: atual?.pacienteId || "",
        rotulo: atual?.data
          ? `Consulta de ${new Date(atual.data).toLocaleDateString("pt-BR")}`
          : "Consulta",
        usuario: { id: usuarioLogado.id, nome: usuarioLogado.nome },
      });

      console.log("✅ Evolução removida com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao remover evolução:", error);
      throw error;
    }
  };

  return (
    <EvolucaoContext.Provider
      value={{
        evolucoes,
        loading,
        adicionarEvolucao,
        listarEvolucoesPorPaciente,
        editarEvolucao,
        cancelarEvolucao,
        removerEvolucao,
        carregarEvolucoes,
      }}
    >
      {children}
    </EvolucaoContext.Provider>
  );
}

export function useEvolucao() {
  const context = useContext(EvolucaoContext);
  if (!context) {
    throw new Error("useEvolucao deve ser usado dentro de EvolucaoProvider");
  }
  return context;
}
